/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import {
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionPanel,
  Grid,
  Link,
  Typography,
  Typography2,
  breakpoints,
} from '@coursera/cds-core';

import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/coding-challenges';

const styles = {
  titleText: css`
    margin-bottom: 40px;
  `,
  faqContentWrapper: css`
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 8px;
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-400) var(--cds-spacing-150);
    }
  `,
  faqContentContainer: css`
    margin-top: var(--cds-spacing-100);
  `,
  accordionItemContainer: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    padding-bottom: var(--cds-spacing-100);
  `,
  sections: css`
    margin: 20px;
    align-items: center;
    justify-content: center;
  `,
  cardsCenter: css`
    justify-content: center;
    width: 83.75%;
  `,
};

const LandingPageFAQ = () => {
  return (
    <div css={styles.cardsCenter}>
      <Typography css={styles.titleText} variant="h1semibold" component="h2">
        {_t('Frequently asked questions')}
      </Typography>
      <Grid css={styles.faqContentWrapper}>
        <AccordionGroup spacing={8}>
          <TrackedDiv trackingName="faq_accordion" css={styles.accordionItemContainer}>
            <Accordion variant="silent">
              <AccordionHeader label="Do I need to pay to register?" />
              <AccordionPanel>
                <Typography2 component="p" variant="bodyPrimary" css={styles.faqContentContainer}>
                  {_t('No. Registration is free.')}
                </Typography2>
              </AccordionPanel>
            </Accordion>
          </TrackedDiv>
          <TrackedDiv trackingName="faq_accordion" css={styles.accordionItemContainer}>
            <Accordion variant="silent">
              <AccordionHeader label="Can I register if I’m not a Coursera learner?" />
              <AccordionPanel>
                <Typography2 component="p" variant="bodyPrimary" css={styles.faqContentContainer}>
                  {_t('No. You will need a Coursera account to register and compete.')}
                </Typography2>
              </AccordionPanel>
            </Accordion>
          </TrackedDiv>
          <TrackedDiv trackingName="faq_accordion" css={styles.accordionItemContainer}>
            <Accordion variant="silent">
              <AccordionHeader label="What should I know going into the challenge?" />
              <AccordionPanel>
                <Typography2 component="p" variant="bodyPrimary" css={styles.faqContentContainer}>
                  {_t(
                    'In this coding challenge, you can test your skills by working with a real-world dataset and building a prediction or classification model using Python in a Jupyter Notebook. After submitting your solution, you can compare and measure your performance against other participants by clicking the "compare my results" link.'
                  )}
                </Typography2>
              </AccordionPanel>
            </Accordion>
          </TrackedDiv>
          <TrackedDiv trackingName="faq_accordion" css={styles.accordionItemContainer}>
            <Accordion variant="silent">
              <AccordionHeader label="How can I prepare for the challenge?" />
              <AccordionPanel>
                <Typography2 component="p" variant="bodyPrimary" css={styles.faqContentContainer}>
                  {_t('We recommend the following courses to help you prepare for this coding challenge:')}
                </Typography2>
                <Typography2 component="p" variant="bodyPrimary" css={styles.faqContentContainer}>
                  <ul>
                    <li>
                      <Link href="/learn/python-machine-learning" variant="quiet" typographyVariant="bodyPrimary">
                        {_t('Applied Machine Learning in Python')}
                      </Link>
                    </li>
                    <li>
                      <Link href="/learn/machine-learning-with-python" variant="quiet" typographyVariant="bodyPrimary">
                        {_t('Machine Learning with Python')}
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/learn/unsupervised-learning-recommenders-reinforcement-learning"
                        variant="quiet"
                        typographyVariant="bodyPrimary"
                      >
                        {_t('Unsupervised Learning, Recommenders, Reinforcement Learning')}
                      </Link>
                    </li>
                  </ul>
                </Typography2>
              </AccordionPanel>
            </Accordion>
          </TrackedDiv>
          <TrackedDiv trackingName="faq_accordion" css={styles.accordionItemContainer}>
            <Accordion variant="silent">
              <AccordionHeader label="Can I share my submission outside of Coursera?" />
              <AccordionPanel>
                <Typography2 component="p" variant="bodyPrimary" css={styles.faqContentContainer}>
                  {_t(
                    'Once you finish your project, you have the option to add it to your Coursera Skills Profile. This way, you can showcase your skills and technical expertise to potential employers.'
                  )}
                </Typography2>
              </AccordionPanel>
            </Accordion>
          </TrackedDiv>
        </AccordionGroup>
      </Grid>
    </div>
  );
};

export default LandingPageFAQ;
